import { z } from 'zod';

export const MCP_READ_SCOPE = 'mcp:read';

/** Query parameters of an OAuth 2.1 authorization request; PKCE with S256 is mandatory. */
export const OAuthAuthorizeParamsSchema = z.object({
  response_type: z.literal('code'),
  client_id: z.string().min(1).max(100),
  redirect_uri: z.string().url().max(2000),
  code_challenge: z.string().regex(/^[A-Za-z0-9_-]{43,128}$/, 'Invalid PKCE code challenge.'),
  code_challenge_method: z.literal('S256'),
  scope: z.string().max(200).optional(),
  state: z.string().max(1000).optional(),
  resource: z.string().url().max(2000).optional(),
});
export type OAuthAuthorizeParams = z.infer<typeof OAuthAuthorizeParamsSchema>;

/** RFC 7591 dynamic client registration request; unknown metadata is ignored. */
export const OAuthClientRegistrationSchema = z.object({
  redirect_uris: z.array(z.string().url().max(2000)).min(1).max(10),
  client_name: z.string().trim().min(1).max(100).optional(),
  token_endpoint_auth_method: z.string().max(100).optional(),
  grant_types: z.array(z.string().max(100)).max(10).optional(),
  response_types: z.array(z.string().max(100)).max(10).optional(),
  scope: z.string().max(200).optional(),
});
export type OAuthClientRegistration = z.infer<typeof OAuthClientRegistrationSchema>;

export const OAuthTokenRequestSchema = z.discriminatedUnion('grant_type', [
  z.object({
    grant_type: z.literal('authorization_code'),
    code: z.string().min(1).max(200),
    redirect_uri: z.string().url().max(2000),
    client_id: z.string().min(1).max(100),
    code_verifier: z.string().regex(/^[A-Za-z0-9._~-]{43,128}$/),
    resource: z.string().url().max(2000).optional(),
  }),
  z.object({
    grant_type: z.literal('refresh_token'),
    refresh_token: z.string().min(1).max(200),
    client_id: z.string().min(1).max(100),
    scope: z.string().max(200).optional(),
    resource: z.string().url().max(2000).optional(),
  }),
]);
export type OAuthTokenRequest = z.infer<typeof OAuthTokenRequestSchema>;

export const OAuthRevokeRequestSchema = z.object({
  token: z.string().min(1).max(2000),
  token_type_hint: z.string().max(50).optional(),
});

export interface OAuthAuthorizePreviewDto {
  clientName: string;
  redirectHost: string;
  scope: string;
}

export interface OAuthRedirectDto {
  redirectTo: string;
}

export interface OAuthGrantDto {
  id: string;
  clientName: string;
  scope: string;
  createdAt: string;
  lastUsedAt: string;
}
